/** Panelden girilen özel kodu belgeye taşır.
 *
 * İki ayrı sözleşme vardır ve karıştırılmamalıdır:
 *
 * **CSS ve JS birer dosyadır.** Sunucu onları kendi adresinden servis eder ve
 * adres içeriğin özetini taşır (`/api/custom/site-<özet>.css`; bkz.
 * `backend/services/custom_code.py`). Burada yapılan iş yalnızca `<link>` ve
 * `<script src>` etiketini doğru yere koymaktır. Kazancı stil tarafındadır:
 * `<link>` sökülüp takılabilir ve adresi değiştiğinde tarayıcı yenisini
 * çeker — yani **CSS değişikliği sayfa yenilemesi istemez**.
 *
 * **Satır içi HTML (header/body/footer) tek kullanımlıktır.** Bir script
 * çalıştıktan sonra bıraktıkları (widget düğümleri, stil etiketleri,
 * zamanlayıcılar, global değişkenler) geri alınamaz. Aynı kodu söküp yeniden
 * eklemek onu ikinci kez çalıştırır; canlı destek gibi widget'ların çoğu
 * ikinci çalıştırmada "zaten yüklüyüm" deyip erken döner ve ekranda stilini
 * hiç almamış ham HTML kalır ("CANLI DESTEK İLE İLETİŞİME GEÇİN" yazan çıplak
 * kutu). Bu yüzden düğümler saklanır: yönetim paneline geçildiğinde DOM'dan
 * sökülür, oyuncu portalına dönüldüğünde **aynı** düğümler geri takılır — bir
 * kez çalışmış script yeniden eklendiğinde çalışmaz (HTML "already started"
 * bayrağı).
 */

// Her satır içi parçanın belgede durduğu yer. Sıra korunur: aynı parçanın
// düğümleri söküldükten sonra tek bir fragment olarak aynı konuma geri girer.
const SLOTS = [
  { key: 'headerCode', parent: () => document.head, position: 'end' },
  { key: 'bodyCode', parent: () => document.body, position: 'start' },
  { key: 'footerCode', parent: () => document.body, position: 'end' },
];

// Varlık etiketleri bu işaretle bulunur: yeniden uygulamada kendi etiketimizi
// tanıyabilmek için gerekir ve panelden girilen kodun eklediği etiketlerle
// karışmaz.
const ASSET_ATTRIBUTE = 'data-bhs-custom';

let applied = null;

const executableFragment = (code) => {
  const template = document.createElement('template');
  template.innerHTML = String(code || '');
  const fragment = template.content;

  // innerHTML/template ile üretilen scriptler güvenlik gereği çalışmaz. Aynı
  // etiketi DOM API ile yeniden oluşturarak panelde girilen izleme/widget
  // kodlarının harici src ve satır içi biçimlerini gerçekten çalıştırırız.
  fragment.querySelectorAll('script').forEach((source) => {
    const script = document.createElement('script');
    Array.from(source.attributes).forEach(({ name, value }) => {
      script.setAttribute(name, value);
    });
    if (source.src && !source.hasAttribute('async')) script.async = false;
    script.textContent = source.textContent;
    source.replaceWith(script);
  });

  return fragment;
};

const buildSlots = (codes) => SLOTS.map((slot) => ({
  ...slot,
  nodes: String(codes[slot.key] || '').trim()
    ? Array.from(executableFragment(codes[slot.key]).childNodes)
    : [],
}));

const isAttached = (slots) => slots.some(({ nodes }) => nodes.some((node) => node.isConnected));

const attach = (slots) => {
  slots.forEach(({ nodes, parent, position }) => {
    if (!nodes.length) return;
    const target = parent();
    if (!target) return;
    const fragment = document.createDocumentFragment();
    nodes.forEach((node) => fragment.appendChild(node));
    if (position === 'start') target.insertBefore(fragment, target.firstChild);
    else target.appendChild(fragment);
  });
};

const detach = (slots) => {
  slots.forEach(({ nodes }) => nodes.forEach((node) => node.parentNode?.removeChild(node)));
};

const findAsset = (kind) => document.querySelector(`[${ASSET_ATTRIBUTE}="${kind}"]`);

const removeAsset = (kind) => findAsset(kind)?.remove();

// Son uygulanan stil adresi. `public/index.html` açılışta bu anahtarı okuyup
// stili React yüklenmeden önce ister; adres içeriğin özetini taşıdığı için
// eskimiş bir değer en kötü ihtimalle 404 alır ve ayarlar gelince yenisiyle
// değiştirilir. Anahtar iki yerde aynı kalmalıdır.
export const CUSTOM_CSS_CACHE_KEY = 'app:custom-css-v1';
// Betik de aynı yoldan erken yüklenir: açılış ekranını (#app-splash) süsleyen
// bir betik ayarları beklerse ekran sönerken çalışır — ekran tam da ayarlar
// gelince kalkar (bkz. lib/splash.js).
export const CUSTOM_JS_CACHE_KEY = 'app:custom-js-v1';

const remember = (key, url) => {
  try {
    if (url) window.localStorage.setItem(key, url);
    else window.localStorage.removeItem(key);
  } catch {
    // Depolama kapalıysa dosya yalnızca ayarlar geldiğinde uygulanır.
  }
};

/** Erken yükleme adreslerini unutur.
 *
 * Kurulum kısıtlıyken portal yerine kilit ekranı çizilir ve özel kod
 * uygulanmaz; adres hatırlanırsa bir sonraki açılışta betik kilit ekranının
 * üstünde çalışırdı.
 */
export const forgetCustomAssets = () => {
  remember(CUSTOM_CSS_CACHE_KEY, '');
  remember(CUSTOM_JS_CACHE_KEY, '');
};

/** Stil dosyasını belgeye koyar, adres değişince tazeler.
 *
 * Etiket `<head>`in **sonuna** eklenir: paneldeki stil tema CSS'ini ve
 * Tailwind utility'lerini ezebilmelidir (bkz. CLAUDE.md → *Giriş kutusu
 * ölçüleri Tailwind'den değil CSS'ten gelir*). Aynı adres ikinci kez
 * verildiğinde etikete dokunulmaz — yeniden eklemek stili bir an için
 * kaldırıp geri koyar ve sayfa gözle görülür biçimde zıplar.
 *
 * Stilin önünde aynı adresi isteyen bir `rel="preload"` etiketi durur ve
 * ikisi de `fetchpriority="high"` taşır: panelden yazılan kural görünümün
 * kendisidir, geç gelirse sayfa önce temanın hâliyle çizilip sonra kayar.
 */
const applyStylesheet = (url) => {
  const current = findAsset('css');
  if (!url) {
    current?.remove();
    removeAsset('css-preload');
    return;
  }
  if (current && current.getAttribute('href') === url) return;
  current?.remove();
  removeAsset('css-preload');
  const preload = document.createElement('link');
  preload.rel = 'preload';
  preload.setAttribute('as', 'style');
  preload.href = url;
  preload.setAttribute('fetchpriority', 'high');
  preload.setAttribute(ASSET_ATTRIBUTE, 'css-preload');
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = url;
  link.setAttribute('fetchpriority', 'high');
  link.setAttribute(ASSET_ATTRIBUTE, 'css');
  document.head.appendChild(preload);
  document.head.appendChild(link);
};

/** Betik dosyasını belgeye koyar.
 *
 * Etiket `defer` **taşımaz** (yönetici isteği, 26.09.2026): dinamik eklenen
 * bir betikte `defer`in zaten etkisi yoktur, belge o an çoktan ayrıştırılmıştır
 * ve betik indirilir indirilmez çalışır. Sonraki açılışlarda aynı betik
 * `public/index.html` tarafından React'tan önce eklenir; adres aynıysa burada
 * etikete dokunulmaz, yani betik ikinci kez çalışmaz. Betiğin bıraktıkları sökülemediği
 * için adres değiştiğinde eski etiket kaldırılır ama etkisi kalır; yönetici
 * konsolda uyarıyı görür.
 */
const applyScript = (url) => {
  const current = findAsset('js');
  if (!url) {
    current?.remove();
    return;
  }
  if (current && current.getAttribute('src') === url) return;
  if (current) {
    current.remove();
    console.warn('Özel JS değişti: eski betiğin bıraktıkları ancak sayfa yenilenince temizlenir.');
  }
  const script = document.createElement('script');
  script.src = url;
  script.setAttribute(ASSET_ATTRIBUTE, 'js');
  document.body.appendChild(script);
};

/** Özel kodu belgeye uygular.
 *
 * `enabled` kapalıyken (yönetim paneli, kilitli kurulum) varlık etiketleri ve
 * satır içi düğümler DOM'dan sökülür; kod hiç uygulanmamışsa da uygulanmaz.
 * Aynı satır içi kod ikinci kez verildiğinde hiçbir şey yapılmaz — düğümler
 * zaten yerindedir, scriptler tekrar çalıştırılmaz.
 */
export const applyCustomCode = ({
  cssUrl = '',
  jsUrl = '',
  headerCode = '',
  bodyCode = '',
  footerCode = '',
  enabled = true,
} = {}) => {
  if (!enabled) {
    removeAsset('css');
    removeAsset('css-preload');
    removeAsset('js');
    if (applied) detach(applied.slots);
    return;
  }

  applyStylesheet(String(cssUrl || ''));
  applyScript(String(jsUrl || ''));
  remember(CUSTOM_CSS_CACHE_KEY, String(cssUrl || ''));
  remember(CUSTOM_JS_CACHE_KEY, String(jsUrl || ''));

  const signature = JSON.stringify([headerCode, bodyCode, footerCode]);

  if (applied && applied.signature !== signature) {
    // Kod gerçekten değişti: eski düğümler gider, yenisi çalıştırılır. Eski
    // scriptin bıraktığı iz sökülemediği için değişikliğin temiz uygulanması
    // sayfa yenilemesine bağlıdır; yönetici konsolda uyarıyı görsün.
    detach(applied.slots);
    applied = null;
    console.warn('Özel kod değişti: eski kodun bıraktıkları ancak sayfa yenilenince temizlenir.');
  }

  if (!applied) applied = { signature, slots: buildSlots({ headerCode, bodyCode, footerCode }) };
  if (!isAttached(applied.slots)) attach(applied.slots);
};

// Testler için: modül durumu sıfırlanır.
export const resetCustomCode = () => {
  if (applied) detach(applied.slots);
  applied = null;
  removeAsset('css');
  removeAsset('css-preload');
  removeAsset('js');
};
